/**
 * The hero device — a real booth, running, next to the prompt box.
 *
 * A page that builds photobooths should show one. This renders the curated
 * booth's actual welcome screen (the same server-side render used as the
 * project thumbnail) inside a phone frame, with its live URL underneath, and
 * cross-fades as the showcase rotates.
 *
 * Renders nothing when the showcase is empty — /new then falls back to the
 * single-column hero it had before.
 */

import React from 'react'
import { FaArrowUpRightFromSquare } from 'react-icons/fa6'
import type { ShowcaseBooth } from '../onboardingApi'
import { publicBoothUrl, publicBoothHostLabel } from '../publicOrigin'

interface HeroBoothProps {
  booth: ShowcaseBooth | null
  /** Kept mounted across rotations so the cross-fade has something to fade. */
  booths: ShowcaseBooth[]
  index: number
}

export const HeroBooth: React.FC<HeroBoothProps> = ({ booth, booths, index }) => {
  if (!booth) return null

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative w-[13.5rem] overflow-hidden rounded-[1.75rem] shadow-2xl ring-1 ring-white/15 sm:w-[15rem]"
        style={{ aspectRatio: '540 / 960', backgroundColor: booth.palette.backgroundColor }}
      >
        {booths.map((b, i) => (
          <img
            key={b.slug}
            src={b.thumbnail}
            alt={i === index ? `${b.title} welcome screen` : ''}
            aria-hidden={i !== index}
            loading={i === 0 ? 'eager' : 'lazy'}
            draggable={false}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        {/* Reads as a screen rather than a flat image. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-white/10"
        />
      </div>

      <a
        href={publicBoothUrl(booth.slug)}
        target="_blank"
        rel="noreferrer"
        className="group inline-flex items-center gap-1.5 text-xs text-whiteout/45 transition-colors hover:text-whiteout"
      >
        <span className="font-mono">
          {publicBoothHostLabel()}/<span className="text-whiteout/75">{booth.slug}</span>
        </span>
        <FaArrowUpRightFromSquare className="text-[9px] opacity-0 transition-opacity group-hover:opacity-100" />
      </a>
    </div>
  )
}

export default HeroBooth
